import { useMutation, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { isDesktop } from "@/helpers/desktop";
import { listPrinters, printReceipt } from "@/helpers/native";
import { printerColumns } from "@/routes/settings/config";

export const usePrinters = () =>
	useQuery({
		queryFn: () => listPrinters(),
		queryKey: ["printers"],
		// The web build has no shell to ask, so do not retry into a guaranteed error.
		enabled: isDesktop(),
	});

interface SavedPrinter {
	name?: string;
	pageSize?: string;
	columns?: number;
}

/**
 * The receipt printer as last saved in the settings. Read from localStorage on
 * each call rather than through `useLocalStorage`, whose copy is taken when
 * the component mounts: the settings page saves and test-prints from the same
 * mount, and the test has to use what was just saved.
 */
const savedPrinter = (): SavedPrinter | undefined => {
	try {
		return JSON.parse(localStorage.getItem("printer") ?? "null") ?? undefined;
	} catch {
		return undefined;
	}
};

/**
 * Characters per line for the currently selected printer. The receipt layout
 * needs it to size the product table's columns.
 */
export const useReceiptColumns = () => printerColumns(savedPrinter());

/** Thrown when no receipt printer has been chosen in the settings. */
export class NoPrinterError extends Error {
	constructor() {
		super("No receipt printer is configured");
		this.name = "NoPrinterError";
	}
}

export const usePrint = () => {
	const { t } = useTranslation();
	const toastId = useRef<string>(null);
	return useMutation({
		mutationFn: (lines: ReceiptLine[]) => {
			const printer = savedPrinter();
			// The Electron build loaded from file://, and Tauri serves the app from
			// another origin, so localStorage starts empty on a till that has just
			// been switched. Labels survived because their port defaults to COM2;
			// receipts have no sensible default, so say what is missing.
			if (!printer?.name) {
				return Promise.reject(new NoPrinterError());
			}
			return printReceipt(printer.name, {
				columns: printerColumns(printer),
				lines,
			});
		},
		onMutate: () => {
			toastId.current = toast.loading(t("global.loading"));
		},
		onSuccess: () => {
			toast.success(t("settings.printSuccess"), {
				id: toastId?.current || undefined,
			});
		},
		onError: (error) => {
			console.error(error);
			toast.error(
				error instanceof NoPrinterError
					? t("settings.noPrinter")
					: t("settings.printError"),
				{ id: toastId?.current || undefined },
			);
		},
	});
};
